import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { History, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import CopilotPanel from "@/components/copilot/CopilotPanel";
import ConversationList from "@/components/copilot/ConversationList";
import { useCopilot } from "@/context/CopilotContext";

// Dedicated Copilot workspace (COP-UX-135) — a first-class primary destination.
// The Dashboard remains the post-login landing (COP-UX-134); this workspace adds
// the conversational surface with shared voice/text context and structured results.
export default function Copilot() {
  const { sessionId, setSessionId } = useCopilot();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const requested = searchParams.get("session");
    if (requested) setSessionId(requested);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // RC51-QA-001 (reopened): the fixed 16rem history rail + 280px nav left the conversation ≈ 53 % of a 1280 px screen.
  // The rail is now collapsible (persisted) and defaults open only on wide desktops (≥ 1440 px).
  const [historyOpen, setHistoryOpen] = useState(() => {
    const saved = typeof window !== "undefined" ? window.localStorage.getItem("zeli-history-open") : null;
    return saved != null ? saved === "1" : (typeof window !== "undefined" && window.innerWidth >= 1440);
  });
  const toggleHistory = () => setHistoryOpen((v) => { window.localStorage.setItem("zeli-history-open", v ? "0" : "1"); return !v; });
  const workspaceRef = useRef(null);
  useEffect(() => {  // I-3 / RC51-QA-001: on mobile (document scroll) pin the workspace to the viewport; on md+ the shell's h-screen main bounds it
    const el = workspaceRef.current;
    if (!el) return undefined;
    const fit = () => {
      if (window.matchMedia("(min-width: 768px)").matches) { el.style.height = ""; return; }
      const top = el.getBoundingClientRect().top + window.scrollY;
      el.style.height = `calc(100dvh - ${Math.max(top, 0) + 16}px)`;
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(document.body);
    window.addEventListener("resize", fit);
    return () => { ro.disconnect(); window.removeEventListener("resize", fit); };
  }, []);

  return (
    <div className="flex min-h-0 flex-1 flex-col" ref={workspaceRef} data-testid="copilot-workspace">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-light tracking-tight md:text-4xl" data-testid="copilot-brand-lockup">
            <span className="text-terracotta">ZELI</span>
          </h1>
          <p className="mt-1 text-xs text-warmgrey">Zelligio Intelligence — the governed conversational layer across Connect</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={toggleHistory} aria-pressed={historyOpen} aria-label={historyOpen ? "Hide conversation history" : "Show conversation history"}
            className="hidden border-limestone text-charcoal hover:bg-sand md:inline-flex" data-testid="copilot-history-toggle">
            <History className="mr-2 h-4 w-4" /> {historyOpen ? "Hide history" : "History"}
          </Button>
          <Button
            variant="outline"
            onClick={() => setSessionId(null)}
            className="border-limestone text-charcoal hover:bg-sand"
            data-testid="copilot-new-conversation"
          >
            <Plus className="mr-2 h-4 w-4" /> New conversation
          </Button>
        </div>
      </div>

      <details className="mb-2 md:hidden" data-testid="copilot-mobile-history">
        <summary className="cursor-pointer text-xs font-medium text-terracotta">Recent conversations</summary>
        <div className="mt-1 rounded-md border border-border bg-white p-1">
          <ConversationList key={sessionId || "fresh"} currentId={sessionId} onSelect={setSessionId} />
        </div>
      </details>
      <div className="flex min-h-0 flex-1 gap-4">
        <aside className={`${historyOpen ? "hidden md:block" : "hidden"} w-56 shrink-0 overflow-y-auto rounded-md border border-border bg-white p-2`} data-testid="copilot-session-list">
          <ConversationList key={sessionId || "fresh"} currentId={sessionId} onSelect={setSessionId} />
        </aside>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col rounded-md border border-border bg-background">
          <CopilotPanel variant="workspace" />
        </div>
      </div>
    </div>
  );
}
